import React, { useState } from "react";
import styles from "./ImageUpload.module.css";

export default function ImageUpload({
  onImageChange,
  onRemove,
  preview,
  label = "Upload Image",
  multiple = false,
  maxFiles = 1,
}) {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleFile = (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);

    // Simulate upload delay for animation
    setTimeout(() => {
      if (multiple) {
        const fileArray = Array.from(files).slice(0, maxFiles);
        onImageChange(fileArray);
      } else {
        onImageChange(files[0]);
      }
      setUploading(false);
    }, 500);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files);
    }
  };

  const handleChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files);
    }
  };

  return (
    <div className={styles.imageUploadContainer}>
      <label className={styles.imageUploadLabel}>{label}</label>
      <div
        className={`${styles.imageUploadBox} ${
          dragActive ? styles.dragActive : ""
        } ${uploading ? styles.uploading : ""}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept="image/*"
          multiple={multiple}
          onChange={handleChange}
          className={styles.imageUploadInput}
          id={`image-upload-${label.replace(/\s+/g, "-")}`}
        />

        {uploading ? (
          <div className={styles.uploadLoading}>
            <div className={styles.spinner}></div>
            <p>Uploading...</p>
          </div>
        ) : (
          <>
            {preview && (
              <div className={styles.imagePreviewContainer}>
                {multiple && Array.isArray(preview) ? (
                  <div className={styles.galleryPreview}>
                    {preview.map((img, idx) => (
                      <div key={idx} className={styles.galleryItem}>
                        <img src={img} alt={`Preview ${idx + 1}`} />
                        <button
                          type="button"
                          className={styles.removeBtn}
                          onClick={() => onRemove(idx)}
                          aria-label="Remove image"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={styles.singlePreview}>
                    <img src={preview} alt="Preview" />
                    <button
                      type="button"
                      className={styles.removeBtn}
                      onClick={onRemove}
                      aria-label="Remove image"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            )}
            {/* Show upload area for gallery even when there are previews, or when no preview for single */}
            <label
              htmlFor={`image-upload-${label.replace(/\s+/g, "-")}`}
              className={styles.uploadPlaceholder}
            >
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
              <p>
                {multiple &&
                preview &&
                Array.isArray(preview) &&
                preview.length > 0
                  ? "Add more images"
                  : "Click to upload or drag and drop"}
              </p>
              <span>
                PNG, JPG, GIF up to 10MB{" "}
                {multiple && `(Max ${maxFiles} images)`}
              </span>
            </label>
          </>
        )}
      </div>
    </div>
  );
}
